import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import PageLayout from "../components/layout/PageLayout";
import { fetchWithAuth } from "../Context/UserContext";
import API_URL from "../config";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const PLANES = ["Gratuita", "Media", "Pro", "Enterprise"];

const StaffCompanyAdmin = () => {
  const [ruc, setRuc] = useState("");
  const [company, setCompany] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setCompany(null);
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/get_company_by_ruc?ruc=${encodeURIComponent(ruc)}`);
      if (response.ok) {
        setCompany(await response.json());
      } else {
        const data = await response.json();
        setError(data.error || "No tienes acceso a esta sección.");
      }
    } catch (err) {
      console.error("Error al buscar la empresa:", err);
      setError("Error en la conexión con el servidor.");
    } finally {
      setLoading(false);
    }
  };

  const handleSetPlan = async (plan) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/company/${company.id_company}/set_plan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });
      if (response.ok) {
        const data = await response.json();
        setCompany(data.company);
        toast.success("Plan actualizado.");
      } else {
        const data = await response.json();
        toast.error(data.error || "No se pudo actualizar el plan.");
      }
    } catch (err) {
      console.error("Error al actualizar el plan:", err);
      toast.error("Error en la conexión con el servidor.");
    }
  };

  const handleToggleCertificado = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/bs/company/${company.id_company}/set_certificado`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ certificado: !company.certificado }),
      });
      if (response.ok) {
        const data = await response.json();
        setCompany(data.company);
        toast.success("Certificación actualizada.");
      } else {
        const data = await response.json();
        toast.error(data.error || "No se pudo actualizar la certificación.");
      }
    } catch (err) {
      console.error("Error al actualizar la certificación:", err);
      toast.error("Error en la conexión con el servidor.");
    }
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Administrar empresas - Procu</title>
      </Helmet>

      <div className="mx-auto max-w-2xl px-4 py-10 sm:px-6">
        <h1 className="text-2xl font-semibold text-foreground">Administrar empresas</h1>
        <p className="text-sm text-muted-foreground">
          Solo visible para el equipo de Procu. Busca una empresa por RUC para cambiar su plan o certificación.
        </p>

        <form onSubmit={handleSearch} className="mt-6 flex gap-2">
          <Input placeholder="RUC" value={ruc} onChange={(e) => setRuc(e.target.value)} />
          <Button type="submit" disabled={loading}>
            {loading ? "Buscando..." : "Buscar"}
          </Button>
        </form>

        {error && <p className="mt-4 text-sm text-destructive">{error}</p>}

        {company && (
          <Card className="mt-6">
            <CardContent className="space-y-4 pt-6">
              <div>
                <h2 className="text-lg font-semibold text-foreground">{company.nombre_empresa}</h2>
                <p className="text-sm text-muted-foreground">RUC: {company.ruc}</p>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Plan</label>
                <select
                  className="h-10 rounded-md border border-border bg-background px-3 text-sm text-foreground"
                  value={company.plan}
                  onChange={(e) => handleSetPlan(e.target.value)}
                >
                  {PLANES.map((plan) => (
                    <option key={plan} value={plan}>
                      {plan}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground">Certificación</label>
                <Button
                  type="button"
                  variant={company.certificado ? "default" : "outline"}
                  onClick={handleToggleCertificado}
                >
                  {company.certificado ? "Certificada — quitar" : "No certificada — marcar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};

export default StaffCompanyAdmin;
